import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  decideTooltipPlacement,
  nearestScrollableAncestor,
  type TooltipPlacement,
} from '@/lib/tooltipPlacement';
import type { TechnicianDaySummary, TechnicianDayState } from '@/lib/availability';
import type { TechnicianDetailStatus } from '@/routes/dispatch/useDispatchBoard';

/**
 * What a dispatcher needs to know about one technician, on demand (M1-39).
 *
 * <b>A disclosure, not a tooltip.</b> Nothing in `web/src` had a hover
 * mechanism before this story — no `title` attributes used as one, no popover,
 * no hover card (confirmed by grep, not assumed) — so this is the first, and it
 * is deliberately built as a button that opens a panel rather than as something
 * that only appears on hover. Three reasons, each of which rules out the
 * hover-only version on its own:
 *
 * 1. <b>WCAG 1.4.13 (Content on Hover or Focus)</b> requires the extra content
 *    to be dismissible, hoverable and persistent. A `title` attribute is none
 *    of the three: it cannot be dismissed without moving the pointer, it
 *    vanishes if you move toward it, and it times out on its own. It is also
 *    unstyleable and announced inconsistently between screen readers.
 * 2. <b>On `pointer: coarse` there is no hover at all.</b> A phone would get no
 *    route to this information whatsoever. The trigger being a real `<button>`
 *    also means it inherits M1-22's 44px touch-target floor for free, rather
 *    than needing its own rule.
 * 3. <b>The board is a drag surface (M1-11).</b> Hover-driven UI on a screen
 *    where the pointer is constantly held down and moved across cells opens
 *    panels nobody asked for, mid-drag.
 *
 * <b>It sits on the column HEADER, not on the cards.</b> The header carries no
 * drag handlers at all — the cards are the drag sources — so the trigger never
 * competes with `useJobDrag`'s pointer capture or its `touch-action: none`.
 * That is also where the AC puts it ("tooltip on each technician"): this is
 * information about a person, not about a job.
 *
 * Hover is kept as an ENHANCEMENT on top of that, for the dispatcher with a
 * mouse who wants a glance rather than a click — gated on
 * `event.pointerType === 'mouse'`, the same Pointer Events discrimination M1-11
 * established. ⚠️ Gating it matters: on touch, `pointerenter` fires immediately
 * before `click`, so an ungated hover-open would open the panel and the tap
 * would then immediately close it again.
 */
/**
 * The visual offset between trigger and panel, in px — Tailwind's `pt-1`/`pb-1`
 * (0.25rem at the default 16px root). Named here because the placement maths
 * has to charge it against whichever side the panel lands on, and a number the
 * decision function did not know about would put the boundary one gap out.
 */
const PANEL_GAP_PX = 4;

export function TechnicianTooltip({
  technicianId,
  name,
  summary,
  detailStatus,
  dayState,
  dayLabel,
  onOpenChange,
  activePanelId,
  className,
}: {
  technicianId: string;
  name: string;
  /** Licences and skills. Undefined while the one board request is in flight, and when it failed. */
  summary: TechnicianDaySummary | undefined;
  /** Which of those two an undefined `summary` is — see `TechnicianDetailStatus`. */
  detailStatus: TechnicianDetailStatus;
  /** Availability for the day this panel is reporting. */
  dayState: TechnicianDayState | undefined;
  /**
   * The day `dayState` is about, already formatted, when that is not obvious
   * from where the panel sits.
   *
   * ⚠️ <b>The week view MUST pass this.</b> Its panel hangs off a row header
   * spanning seven days while reporting only the first of them, so an
   * unqualified "this day" there names nothing — and since `boardWeek` starts
   * weeks on SUNDAY, the day it silently meant was the one an ordinary
   * Monday-to-Friday technician is least likely to be working. The day view
   * omits it: there is exactly one day on screen and the board's own date
   * navigation is already naming it.
   */
  dayLabel?: string;
  /**
   * Told whenever the panel opens or closes.
   *
   * ⚠️ <b>Load-bearing on the week board, not a convenience.</b> Both boards
   * put this component inside a `position: sticky` heading, which creates a
   * stacking context that traps the panel's own `z-index`. On the day board
   * that is fixed once and statically, by giving the heading a `z-index` above
   * the job list. The week board cannot do that: its competitor is the NEXT
   * technician's heading, which carries the identical class, so DOM order
   * decides and every row below the open one paints over the panel. Only the
   * heading that currently holds an open panel can be raised, and it is the
   * board — not this component — that owns that class.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Which technician's panel the board currently considers open.
   *
   * ⚠️ Only ONE may be, and the reason is the same stacking one
   * `onOpenChange` exists for: the board can raise exactly one heading, so a
   * second panel left open underneath would be the covered-panel bug again,
   * just harder to reproduce. A click elsewhere already closes this panel
   * through the document `pointerdown` listener; opening another by KEYBOARD
   * fires no pointer event at all, which is the gap this closes.
   */
  activePanelId?: string | null;
  className?: string;
}) {
  const { t } = useTranslation();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  /**
   * How the panel currently on screen was opened.
   *
   * ⚠️ Without this, `pointerleave` closed a panel the user had deliberately
   * clicked open, so there was no way to pin one and look away — and a
   * keyboard user whose cursor happened to rest on the header lost the panel
   * to a mouse movement they never made. Hover opened it, so hover may close
   * it; nothing else may.
   */
  const openedBy = useRef<'hover' | 'press' | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Which side the panel is on (M1-40). Starts below every time it opens,
   * because below is what the reader expects and because the panel has to
   * exist before its height can be measured.
   */
  const [placement, setPlacement] = useState<TooltipPlacement>('below');

  const clearHoverTimer = () => {
    if (hoverTimer.current !== null) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  const close = useCallback(() => {
    clearHoverTimer();
    openedBy.current = null;
    setOpen(false);
  }, []);

  // Dismissible (1.4.13), and WITHOUT moving focus: Escape closes the panel and
  // leaves the trigger where it was, so a keyboard user does not lose their
  // place in a board 25 columns wide.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        close();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, close]);

  useEffect(() => clearHoverTimer, []);

  // Reported rather than pushed from each call site, so the board is told about
  // Escape and click-outside too, not only about the trigger being pressed.
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  /**
   * Measure, then decide (M1-40).
   *
   * ⚠️ <c>useLayoutEffect</c>, not <c>useEffect</c>: it runs after the DOM is
   * written but BEFORE the browser paints, so a panel that needs to flip is
   * never shown below first. With <c>useEffect</c> the wrong placement reaches
   * the screen for one frame and the panel visibly jumps — worst on the week
   * board, which is the one that always flips.
   *
   * The panel's own height cannot be known before it renders, so the first
   * pass is genuinely "render below, measure, correct". That is one layout
   * pass, not a loop: <c>placement</c> only ever changes from the value the
   * measurement implies, and the measurement does not depend on it — the
   * trigger and container rects are unaffected by which side the panel sits.
   *
   * Re-runs on <c>summary</c> and <c>dayState</c> because the panel's height
   * is a function of its content: a technician whose licences and skills load
   * in after the panel opens gets a taller panel than the one that was
   * measured, and M1-27's whole finding was that a fourth section changed the
   * answer.
   */
  const measureAndPlace = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) {
      return;
    }

    const container = nearestScrollableAncestor(trigger);
    const triggerRect = trigger.getBoundingClientRect();
    const panelHeight = panel.getBoundingClientRect().height;

    // No clipping ancestor at all means the viewport is the only bound.
    const containerRect = container?.getBoundingClientRect() ?? {
      top: 0,
      bottom: window.innerHeight,
    };

    // ⚠️ Clamped to the VIEWPORT, and this is not belt-and-braces — the day
    // board is why. Its clipping container is real (`overflow-x-auto`, which
    // computes `overflow-y: auto`), but measured 929px tall against an 860px
    // window: it extends 619px below the fold at a short viewport. Asking only
    // "does the panel fit inside the container" therefore answered yes while
    // 144 of 1824 probes were off-screen entirely. The space that exists is the
    // INTERSECTION of what clips and what is on screen; anything else is a
    // panel that technically fits somewhere nobody is looking.
    //
    // Found in the browser pass, not in a test — jsdom has no viewport to run
    // out of.
    const visibleTop = Math.max(containerRect.top, 0);
    const visibleBottom = Math.min(containerRect.bottom, window.innerHeight);

    setPlacement(
      decideTooltipPlacement({
        triggerTop: triggerRect.top,
        triggerBottom: triggerRect.bottom,
        containerTop: visibleTop,
        containerBottom: visibleBottom,
        panelHeight,
        gap: PANEL_GAP_PX,
      }),
    );
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      // Back to the default, so the next open does not inherit a decision made
      // about a different trigger's position after the board scrolled.
      setPlacement('below');
      return;
    }

    measureAndPlace();
  }, [open, summary, dayState, measureAndPlace]);

  /**
   * ⚠️ <b>Re-measure while the panel is open, because the geometry it was
   * placed from does not stay true.</b>
   *
   * Placing once at open is correct for exactly as long as nothing moves. Open
   * the last row's panel on the week board — it flips above — then wheel-scroll
   * the container so that row rises toward the top, and the panel is now
   * hanging off the container's TOP edge: the same clipping this story exists
   * to remove, mirrored. Nothing recovered it, because a wheel scroll fires
   * neither of the two events that close this panel (Escape, and a document
   * <c>pointerdown</c>), so it simply stayed open on a stale side.
   *
   * <b>Re-measuring rather than closing on scroll, and that is M1-27's call
   * rather than a preference.</b> M1-27 made the scroll container focusable
   * precisely so somebody could SCROLL to reach panel content that did not
   * fit; dismissing the panel on scroll would delete that remedy, and would
   * work against 1.4.13's "persistent" clause the rest of this component is
   * built around.
   *
   * ⚠️ No <c>requestAnimationFrame</c> throttle. Three rect reads on a scroll
   * that only happens while a panel is open is cheap, and rAF does not fire at
   * all in a hidden browser pane — which is why the board's own drag
   * auto-scroll is a <c>setInterval</c> (web/CONVENTIONS.md). A throttle that
   * makes the behaviour unverifiable costs more than it saves here.
   *
   * The page scroll listener is not redundant with the container's: the day
   * board's container is taller than the viewport and moves with the PAGE,
   * so its panel goes stale on a window scroll that the container never sees.
   */
  useEffect(() => {
    if (!open) {
      return;
    }

    const container = nearestScrollableAncestor(triggerRef.current);
    const onGeometryChange = () => measureAndPlace();

    container?.addEventListener('scroll', onGeometryChange, { passive: true });
    window.addEventListener('scroll', onGeometryChange, { passive: true });
    window.addEventListener('resize', onGeometryChange);
    return () => {
      container?.removeEventListener('scroll', onGeometryChange);
      window.removeEventListener('scroll', onGeometryChange);
      window.removeEventListener('resize', onGeometryChange);
    };
  }, [open, measureAndPlace]);

  // Someone else's panel is the open one now, so this is not.
  useEffect(() => {
    if (open && activePanelId != null && activePanelId !== technicianId) {
      close();
    }
  }, [open, activePanelId, technicianId, close]);

  return (
    <div
      ref={wrapperRef}
      className={cn('relative', className)}
      // Hoverable (1.4.13): the listeners are on the WRAPPER, which contains
      // the panel, so moving the pointer off the trigger and into the panel
      // never crosses a boundary that would close it. On the trigger alone,
      // reading the panel would be impossible with a mouse.
      onPointerEnter={(event) => {
        if (event.pointerType !== 'mouse') {
          return;
        }
        clearHoverTimer();
        // A short delay, so dragging a card across a row of headers does not
        // open five panels on the way past.
        hoverTimer.current = setTimeout(() => {
          openedBy.current = 'hover';
          setOpen(true);
        }, 150);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType !== 'mouse') {
          return;
        }
        clearHoverTimer();
        // Only what hover opened. A panel the user clicked or pressed Enter on
        // stays until they dismiss it.
        if (openedBy.current === 'hover') {
          openedBy.current = null;
          setOpen(false);
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        // The name alone would make 25 buttons whose accessible names are just
        // people's names, with nothing saying what pressing one does.
        aria-label={t('dispatch.technicianDetails.trigger', { name })}
        onClick={() => {
          clearHoverTimer();
          setOpen((previous) => {
            openedBy.current = previous ? null : 'press';
            return !previous;
          });
        }}
        className="flex w-full items-center gap-1 rounded text-left focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
        data-testid={`technician-details-trigger-${technicianId}`}
      >
        <span className="truncate text-sm font-medium">{name}</span>
        <span aria-hidden="true" className="text-[0.625rem] text-[var(--color-muted-foreground)]">
          ⓘ
        </span>
      </button>

      {open ? (
        // ⚠️ The visual 4px offset below the trigger is PADDING on this
        // positioned wrapper, not `margin-top` on the panel — and that is the
        // whole of 1.4.13's "hoverable" clause, not a style preference. A
        // margin puts those four pixels outside both the trigger and the panel
        // while belonging to neither, so a pointer travelling from one to the
        // other hit-tests something else on the way; `pointerleave` fires on
        // this component's own wrapper and the panel closes before the pointer
        // arrives. Measured in a browser: driving a mouse down through the gap
        // logged `pointerleave` at the exact y the strip occupied, and the
        // panel was gone. Padding keeps the strip inside the wrapper's
        // subtree, so the crossing is continuous.
        //
        // It fails intermittently rather than always — a fast flick can skip
        // the band between two pointermove samples — which inverts who it hurts:
        // the slow, deliberate pointer always lands in it, and slow deliberate
        // pointers are exactly who 1.4.13 exists for.
        <div
          className={cn(
            'absolute left-0 z-30 w-60',
            // ⚠️ The gap stays PADDING on this wrapper on BOTH sides — pt-1
            // below, pb-1 above — for the 1.4.13 reason above. Flipping to a
            // margin on the upward case would reintroduce exactly the
            // discontinuity the comment describes, on the side the week board
            // actually uses.
            placement === 'below' ? 'top-full pt-1' : 'bottom-full pb-1',
          )}
          data-testid={`technician-details-wrapper-${technicianId}`}
          data-placement={placement}
        >
          {/* Persistent (1.4.13): nothing here hides itself on a timer. It
              stays until Escape, a click outside, the trigger again, or — for
              a panel hover opened — the pointer leaving the whole wrapper. */}
          <div
            ref={panelRef}
            id={panelId}
            className="w-full space-y-2 rounded-md border bg-[var(--color-background)] p-3 text-left shadow-lg"
            data-testid={`technician-details-${technicianId}`}
          >
            <p className="text-sm font-medium">{name}</p>

            <Section title={t('dispatch.technicianDetails.availability')}>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                {describeAvailability(dayState, dayLabel, t)}
              </p>
            </Section>

            {/* The AC: "a tech's current vehicle shows on … the dispatch
                board tooltip". It arrives on the SAME per-day cell the
                availability line above reads, from the same single windowed
                request — a separate per-technician lookup would have been the
                N+1 that endpoint exists to avoid, twenty-five times over.

                ⚠️ Reported per DAY, which is why it sits beside availability
                rather than beside licences: an assignment is effective-dated,
                so the van changes across a week the way the working hours do
                and unlike a licence. */}
            <Section title={t('dispatch.technicianDetails.vehicle')}>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                {describeVehicle(dayState, t)}
              </p>
              {/* ⚠️ The same three facts, in the same words, as
                  TechnicianVehicleSection's row on the profile page — label
                  and plate, then whether they drive it, then whether it is on
                  the road. Both surfaces answer "what is this person driving",
                  and this one used to answer it with less: `vehicleStatus` and
                  `vehicleIsPrimaryDriver` crossed the wire and NOTHING read
                  them, so retiring a truck under a standing assignment left
                  the tooltip reading "Van 12 · ABC-123" with no indication it
                  was off the road, while the tool search and the profile both
                  said "Retired". */}
              {dayState?.vehicleLabel && dayState.vehicleIsPrimaryDriver ? (
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  {t('technicianVehicles.primary')}
                </p>
              ) : null}
              {/* Only where the warning below is not already saying it: a
                  RETIRED truck, or an in-maintenance one this person is not
                  the primary driver of. Both are statements of fact rather
                  than warnings, which is exactly the AC's distinction —
                  the warning is scoped to "its primary tech", the truck being
                  off the road is not. */}
              {dayState?.vehicleLabel &&
              dayState.vehicleStatus &&
              dayState.vehicleStatus !== 'Active' &&
              !dayState.vehicleMaintenanceWarning ? (
                <p className="text-xs font-medium text-[var(--color-destructive)]">
                  {t(`fleet.status.${dayState.vehicleStatus}`)}
                </p>
              ) : null}
              {dayState?.vehicleMaintenanceWarning ? (
                // The same words-carry-the-meaning rule the licence line
                // follows, and the same already-verified --color-destructive
                // pairing. No new palette token. ⚠️ It is a statement, not a
                // refusal: nothing on this screen stops the dispatcher
                // scheduling the job anyway (M1-12).
                <p className="text-xs text-[var(--color-destructive)]">
                  {t('dispatch.technicianDetails.vehicleInMaintenance')}
                </p>
              ) : null}
            </Section>

            <Section title={t('dispatch.technicianDetails.licenses')}>
              {summary ? (
                summary.licenses.length > 0 ? (
                  <ul className="space-y-0.5">
                    {summary.licenses.map((license) => (
                      <li key={license.label} className="text-xs">
                        {license.label}
                        {license.expiryState === 'Expired' ||
                        license.expiryState === 'ExpiringSoon' ? (
                          // The same words-carry-the-meaning rule M1-37's badge
                          // follows, and the same already-verified
                          // `--color-destructive` pairing. No new palette token.
                          <span className="ml-1 text-[var(--color-destructive)]">
                            {t(`dispatch.technicianDetails.expiry.${license.expiryState}`)}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    {t('dispatch.technicianDetails.noLicenses')}
                  </p>
                )
              ) : (
                // ⚠️ NOT "No licences recorded." An undefined summary means the
                // one board request has not landed or did not come back, and
                // saying "none" there is a positive claim nobody made — about
                // exactly the thing a dispatcher checks before handing somebody
                // regulated work. A technician with an expired Master licence
                // read as unlicensed-and-fine.
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  {t(
                    detailStatus === 'pending'
                      ? 'dispatch.technicianDetails.detailsLoading'
                      : 'dispatch.technicianDetails.licensesUnavailable',
                  )}
                </p>
              )}
            </Section>

            <Section title={t('dispatch.technicianDetails.skills')}>
              {summary ? (
                summary.skills.length > 0 ? (
                  <ul className="space-y-0.5">
                    {summary.skills.map((skill) => (
                      <li key={skill.label} className="text-xs">
                        {skill.label}
                        <span className="ml-1 text-[var(--color-muted-foreground)]">
                          {t(`dispatch.technicianDetails.proficiency.${skill.proficiency}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    {t('dispatch.technicianDetails.noSkills')}
                  </p>
                )
              ) : (
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  {t(
                    detailStatus === 'pending'
                      ? 'dispatch.technicianDetails.detailsLoading'
                      : 'dispatch.technicianDetails.skillsUnavailable',
                  )}
                </p>
              )}
            </Section>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      {/* Not a heading element: this panel opens inside a column's own <h3>,
          and an <h4> here would put headings inside a heading. A styled label
          says the same thing without breaking the document outline axe checks. */}
      <p className="text-[0.625rem] font-medium tracking-wide text-[var(--color-muted-foreground)] uppercase">
        {title}
      </p>
      {children}
    </div>
  );
}

/**
 * The reported day's availability, as a sentence.
 *
 * Exported for the tests, which assert the sentence rather than the state
 * name — a dispatcher reads the sentence, and a test that asserted the enum
 * would pass on a string nobody can understand.
 *
 * ⚠️ `dayLabel` is not decoration. Where the panel does not sit under a single
 * visible date — the week view, whose row header spans seven — an unqualified
 * "this day" is a sentence with no referent, and the day it silently meant was
 * the week's Sunday.
 */
/**
 * Which vehicle this person is in on the reported day, as a sentence (M1-27).
 *
 * Exported for the tests, which assert the sentence rather than the field — a
 * dispatcher reads the sentence.
 *
 * ⚠️ <b>No vehicle is an ABSENCE, and it says so plainly rather than saying
 * nothing.</b> Every technician is in that state the day this ships, so a
 * silent blank would be the commonest rendering of this section by far — and
 * a section that is usually empty reads as broken rather than as informative.
 * That is the same lesson M1-39 recorded for its own `Unknown`: a default that
 * is deliberately invisible still has to be legible.
 *
 * It deliberately does NOT need `dayLabel` the way the availability line does.
 * The availability line has three different sentences that each name a day;
 * this one names a truck, and "Van 3" qualified by a date would be claiming
 * more precision than the reader needs from a panel they opened to see who is
 * driving what.
 */
export function describeVehicle(
  dayState: TechnicianDayState | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (!dayState?.vehicleLabel) {
    return t('dispatch.technicianDetails.noVehicle');
  }
  return dayState.vehiclePlate
    ? t('dispatch.technicianDetails.vehicleWithPlate', {
        label: dayState.vehicleLabel,
        plate: dayState.vehiclePlate,
      })
    : dayState.vehicleLabel;
}

export function describeAvailability(
  dayState: TechnicianDayState | undefined,
  dayLabel: string | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const qualified = (key: string, options: Record<string, unknown> = {}) =>
    dayLabel
      ? t(`dispatch.technicianDetails.${key}On`, { ...options, day: dayLabel })
      : t(`dispatch.technicianDetails.${key}`, options);

  if (!dayState || dayState.availabilityState === 'Unknown') {
    // Says the honest thing rather than "unavailable". Nobody has recorded
    // hours for this person, which is not a claim about whether they are
    // working.
    return t('dispatch.technicianDetails.availabilityUnknown');
  }
  if (dayState.availabilityState === 'TimeOff') {
    return qualified('availabilityTimeOff', {
      kind: t(`dispatch.technicianDetails.timeOffKind.${dayState.timeOffKind ?? 'Other'}`),
    });
  }
  if (dayState.availabilityState === 'OutsideTemplate') {
    return qualified('availabilityOutsideTemplate');
  }
  return qualified('availabilityWorking', {
    start: dayState.startTime,
    end: dayState.endTime,
  });
}
